import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { ApiError, api, type Condominio } from "../lib/api";

interface Props {
  onCancelar: () => void;
  onCriado: (id: number) => void;
}

interface Form {
  nome: string;
  nome_pasta: string;
  numero_unidades: number;
  cnpj: string;
  endereco: string;
  sindico_nome: string;
  sindico_email: string;
}

const INICIAL: Form = {
  nome: "",
  nome_pasta: "",
  numero_unidades: 1,
  cnpj: "",
  endereco: "",
  sindico_nome: "",
  sindico_email: "",
};

export function NovoCondominioPage({ onCancelar, onCriado }: Props) {
  const [form, setForm] = useState<Form>(INICIAL);
  const qc = useQueryClient();

  const criar = useMutation({
    mutationFn: (payload: Form) =>
      api.post<Condominio>("/condominios", {
        ...payload,
        cnpj: payload.cnpj || null,
        endereco: payload.endereco || null,
        sindico_nome: payload.sindico_nome || null,
        sindico_email: payload.sindico_email || null,
      }),
    onSuccess: (cond) => {
      qc.invalidateQueries({ queryKey: ["condominios"] });
      onCriado(cond.id);
    },
  });

  const podeEnviar = form.nome.trim() && form.nome_pasta.trim() && form.numero_unidades > 0;

  return (
    <div className="max-w-2xl mx-auto">
      <button
        type="button"
        onClick={onCancelar}
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div className="bg-white border border-slate-200 rounded-card p-6">
        <h2 className="text-xl font-semibold text-slate-900">Cadastrar novo condomínio</h2>
        <p className="text-sm text-slate-500 mt-1">
          Após criar, será gerada a pasta <code className="px-1 bg-slate-100 rounded">SindicoAPP-{form.nome_pasta || "{nome}"}/</code>.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate(form);
          }}
          className="mt-6 space-y-4"
        >
          <Field label="Nome do condomínio" required>
            <input
              required
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="input"
              placeholder="Ex.: Condomínio Edifício Golden Garden"
            />
          </Field>

          <Field label="Nome curto (pasta)" required hint="Apenas letras, números, hífen e underscore (sem espaços).">
            <input
              required
              pattern="[A-Za-z0-9_\-]+"
              value={form.nome_pasta}
              onChange={(e) => setForm({ ...form, nome_pasta: e.target.value })}
              className="input"
              placeholder="GoldenGarden"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nº de unidades" required>
              <input
                required
                type="number"
                min={1}
                value={form.numero_unidades}
                onChange={(e) => setForm({ ...form, numero_unidades: Number(e.target.value) })}
                className="input"
              />
            </Field>
            <Field label="CNPJ">
              <input
                value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                className="input"
                placeholder="00.000.000/0000-00"
              />
            </Field>
          </div>

          <Field label="Endereço">
            <input
              value={form.endereco}
              onChange={(e) => setForm({ ...form, endereco: e.target.value })}
              className="input"
              placeholder="Rua / Av., número, cidade"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nome do síndico">
              <input
                value={form.sindico_nome}
                onChange={(e) => setForm({ ...form, sindico_nome: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="E-mail do síndico">
              <input
                type="email"
                value={form.sindico_email}
                onChange={(e) => setForm({ ...form, sindico_email: e.target.value })}
                className="input"
              />
            </Field>
          </div>

          {criar.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              {criar.error instanceof ApiError
                ? `${criar.error.status}: ${(criar.error.body as { detail?: string })?.detail ?? criar.error.message}`
                : "Erro ao criar condomínio"}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onCancelar}
              className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-md text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!podeEnviar || criar.isPending}
              className="px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              {criar.isPending ? "Criando..." : "Criar condomínio"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid rgb(203 213 225);
          border-radius: 6px;
          font-size: 14px;
        }
        .input:focus {
          outline: none;
          border-color: rgb(29 78 216);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.1);
        }
      `}</style>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}
